import type { UrlObject } from 'url'
import { Box, Button, Stack, type TypographyProps } from '@chakra-ui/react'
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'

import ShwLink from '@/components/ShwLink'

const variants = ['blue', 'light'] as const
type TextButtonWithChevronVariant = (typeof variants)[number]

export default function TextButtonWithChevron({
  title,
  variant = 'blue',
  href = null,
  direction = 'right',
  fontSize = '16px',
}: {
  title: string
  variant?: TextButtonWithChevronVariant
  href?: string | UrlObject | null
  direction?: 'left' | 'right'
  fontSize?: TypographyProps['fontSize']
}): ReactElement {
  if (!variants.includes(variant)) {
    throw new Error(`${variant} is not assignable to type TextButtonWithChevronVariant`)
  }

  const config = {
    blue: {
      base: 'primary.solid',
      hover: 'primary.fg',
      active: 'primary.emphasized',
    },
    light: {
      base: 'gray.contrast',
      hover: 'gray.contrast',
      active: 'gray.contrast',
    },
  }

  const content = (
    <Button
      variant="link"
      as={href == null ? 'div' : 'a'}
      textDecoration="none"
      color={config[variant].base}
      _hover={{
        color: config[variant].hover,
        cursor: 'pointer',
        '.chevron-icon': {
          transform: `translateX(${direction === 'right' ? '' : '-'}4px)`,
          transition: '0.15s ease-in-out',
        },
      }}
      _active={{
        color: config[variant].active,
      }}
      role="group"
      fontWeight={500}
      fontSize={fontSize}
    >
      <Stack direction="row" spacing="8px" align="center">
        {direction === 'left' ? (
          <Box className="chevron-icon">
            <FontAwesomeIcon icon={faChevronLeft} size="sm" />
          </Box>
        ) : null}
        <Box fontWeight={600}>{title}</Box>
        {direction === 'right' ? (
          <Box className="chevron-icon">
            <FontAwesomeIcon icon={faChevronRight} size="sm" />
          </Box>
        ) : null}
      </Stack>
    </Button>
  )

  if (href != null) {
    return <ShwLink href={href}>{content}</ShwLink>
  }

  return content
}
